import React, { useState, useRef } from 'react';
import { Typography, Box, Container, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, Formik, FormikProps } from 'formik';
import { googleRegistrationSchema, initialValues, registrationSchema } from './helper';
import { GoogleButton } from '../../../components/auth/GoogleButton';
import {
  fetchGoogleSession,
  googleErrorCode,
  googleMessage,
  googleRegisterExpert,
} from '../../../components/auth/googleSignIn';
import { rememberSignInMethod } from '../../../utils/lastSignInMethod';
import BasicInfo from './basic';
import Demographics from './demographics';
import ContactInfo from './contact';
import Qualifications from './qualifications';
import ExpertiseInfo from './expertise';
import SocialReach from './social';
import { uploadImage } from '../../profile/helper';
import { useUser } from '../../../hooks/useUser';
import axiosInstant from '../../../api';
import { createExpert } from '../../../api/endpoints';
import { SystemRoles } from '../../../providers/roles';
import { uploadVideo } from '../recording/helper';
import { useBoundStore } from '../../../store';
import { ReactComponent as JoinCommunity } from '../../../assets/join_community_logo.svg';
import { DotLottleLoader } from '../../../components/Loader';
import Professions from './profession';

// Set by GoogleReturnPage when a "Continue with Google" application comes
// back here: the verified email (locked; the API takes it from its own
// session) and the names Google supplied.
interface GoogleApplication {
  flowId: string;
  email: string;
  firstName: string;
  lastName: string;
}

export default function ApplicationForm() {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const google = (location.state as { google?: GoogleApplication } | null)?.google;
  const [avatar, setAvatar] = useState<any>({});
  const [introVideo, setIntroVideo] = useState<any>();
  const [submissionError, setSubmissionError] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  const login = useBoundStore((state) => state.login);
  const autoLogin = useBoundStore((state) => state.autoLogin);
  const professionsFormRef = useRef<{ mergePendingItems: () => any[] }>(null);
  // The Google redirect leaves the page and clears the form, so the button
  // asks first when anything was entered — read when it is clicked.
  const formikRef = useRef<FormikProps<typeof initialValues>>(null);
  const hasEnteredAnything = () => !!formikRef.current?.dirty || !!avatar.file || !!introVideo;
  const confirmLeaving = (
    message = 'Continuing with Google reloads this page and clears what you have entered so far. Continue?',
  ) => !hasEnteredAnything() || window.confirm(message);

  const textStyles = {
    color: '#FFF',
    fontStyle: 'normal',
    fontSize: { xs: '24px', sm: '24px', md: '24px' },
    fontWeight: 500,
    lineHeight: { xs: '36px', sm: '36px', md: '36px' },
    textTransform: 'uppercase',
    margin: { xs: '0px', sm: '32px 0px' },
    whiteSpace: 'normal',
  };

  const highlightTextStyles = {
    ...textStyles,
    fontSize: { xs: '24px', sm: '24px', md: '24px' },
    fontWeight: 500,
    lineHeight: '36px',
  };

  // Same landing for both paths: the MSA tab until it is signed, else home.
  const landAfterSignIn = () => {
    const { user: signedIn } = useBoundStore.getState();
    if (signedIn?.usertype === 'verified creator' && !signedIn?.msaAgreementSigned) {
      navigate('/profile?tab=MSA');
    } else {
      navigate('/');
    }
  };

  const goToAccount = async (userValues: any) => {
    if (!userValues?.emailAddress || !userValues?.password) {
      console.error('Email or Password missing for login');
      return;
    }
    await login(userValues.emailAddress, userValues.password);
    landAfterSignIn();
  };

  const submit = async (values: any, actions: any) => {
    // Merge pending items before submitting
    const mergedProfessions = professionsFormRef.current?.mergePendingItems() || values.professions;
    const updatedValues = { ...values, professions: mergedProfessions };
    
    const { file, extension } = avatar;
    setSubmissionError('');

    if (!avatar.file || !avatar.extension) {
      setAvatar((prev: any) => ({ ...prev, error: 'image is required field' }));
      actions.setSubmitting(false);
      return;
    }

    // Google mode: make sure the Google sign-in is still good BEFORE the
    // photo and video upload, so an expired or replaced one doesn't cost an
    // upload. The password application is unchanged.
    if (google) {
      setLoadingMessage('Checking your Google sign-in');
      let problem: string | null = null;
      try {
        const current = await fetchGoogleSession();
        if (current.flowId !== google.flowId) problem = 'FlowChanged';
        // An account with this email now exists (made in another tab, or
        // a half-finished earlier try): log in, don't sign up again.
        else if (current.next === 'link' || current.next === 'login') problem = 'AccountExists';
        else if (current.next !== 'register') problem = current.code || 'GoogleSignInFailed';
      } catch (err) {
        problem = googleErrorCode(err);
      }
      if (problem) {
        setLoadingMessage('');
        setSubmissionError(googleMessage(problem));
        actions.setSubmitting(false);
        return;
      }
    }

    setLoadingMessage('Uploading the image');
    const imageLink = await uploadImage(file, user?.userid || '', extension);
    if (!imageLink) {
      setSubmissionError('Image is not uploaded properly');
      actions.setSubmitting(false);
      return;
    }

    let video1: string | undefined;
    if (introVideo) {
      setLoadingMessage('Uploading the introductory video');
      const videoUrl = await uploadVideo(introVideo, 'video1', true);
      if (!videoUrl) {
        setSubmissionError('Video is not uploaded properly');
        actions.setSubmitting(false);
        return;
      }
      video1 = videoUrl;
    }

    setLoadingMessage('');
    const isParent = values.isParent === 'Yes';
    const openToPromotional = values.openToPromotional === 'Yes';
    const firstName = values.firstName.trim();
    const lastName = values.lastName.trim();
    const fullName = `${firstName} ${lastName}`.trim();

    const payload = {
      ...updatedValues,
      avatar: imageLink,
      usertype: SystemRoles.PRO,
      displayName: fullName,
      firstName,
      lastName,
      video1,
      isParent,
      openToPromotional,
    };

    try {
      if (google) {
        // The API reads the email from its session and ignores any in the
        // body; password fields were never shown.
        const { emailAddress, password, passwordConfirmation, msaAgreementSigned, usertype, ...fields } =
          payload;
        const signedIn = await googleRegisterExpert(google.flowId, fields);
        await autoLogin({ tokens: signedIn.tokens });
        rememberSignInMethod('google');
        landAfterSignIn();
        return;
      }
      await axiosInstant.post(createExpert(), payload);
      await goToAccount(values);
    } catch (err: any) {
      const errorMessage = err?.response?.data;
      if (google && errorMessage !== 'EmailAlreadyExists') {
        setSubmissionError(googleMessage(googleErrorCode(err)));
      } else if (errorMessage === 'EmailAlreadyExists') {
        setSubmissionError(`Hello! We received a previous onboarding form submission from your email address, and you are already in our database. Our team will reach out to confirm your interest and availability for new category-specific opportunities, as they come through our pipeline.
        Please try <a href='/login'>logging in</a> or <a href='/reset-password'>reset your password</a>, and reach out to us if you have any questions.
        Thank you!\n`);
      } else {
        setSubmissionError((err as Error).message);
      }
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <div>
      <Box
        sx={{
          backgroundColor: 'black',
          height: '451px',
          position: 'relative',
          paddingBottom: '20px',
        }}
      >
        <Container>
          <Typography
            color="#FFF"
            sx={{
              paddingTop: '59px',
              textAlign: { xs: 'center', md: 'left' },
              whiteSpace: 'normal',
              marginBottom: '20px',
              display: { xs: 'flex', md: 'block' },
              gap: { xs: '20px', md: 0 },
            }}
          >
            <JoinCommunity />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                gap: '6px',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'center', sm: 'flex-start' },
                flexWrap: { xs: 'wrap', sm: 'none' },
                marginTop: { xs: '12px', sm: '0px' },
              }}
            >
              <Typography sx={textStyles}>Join</Typography>
              <Typography sx={highlightTextStyles}>
                The Desire Company
              </Typography>
              <Typography sx={textStyles}>Community</Typography>
            </Box>
          </Typography>
        </Container>
      </Box>

      <Box
        sx={{
          backgroundColor: 'var(--page)',
          paddingTop: '120px',
          paddingBottom: '40px',
        }}
      >
        <Container>
          <Box
            padding="16px"
            marginTop="-320px"
            sx={{
              backgroundColor: 'var(--surface)',
              borderRadius: '16px',
              position: 'relative',
              zIndex: 2,
              boxShadow: 'var(--sh-2)',
            }}
          >
            {!google && (
              <Box className="max-w-[360px] mx-auto mt-4 mb-2">
                <GoogleButton intent="expert" onBeforeStart={() => confirmLeaving()} />
              </Box>
            )}
            {google && (
              <Box className="flex flex-col items-center gap-2 mt-4">
                <Typography className="text-text-2 text-center text-sm">
                  Applying with Google as {google.email}. The email below comes from Google; no password is needed.
                </Typography>
                {/* Always a way out of Google mode: it lives in the history
                    entry, so a reload alone keeps it. */}
                <Box className="flex flex-wrap justify-center items-center gap-3">
                  <Box className="w-[240px]">
                    <GoogleButton intent="expert" label="Start again with Google" onBeforeStart={() => confirmLeaving()} />
                  </Box>
                  <Button
                    variant="text"
                    onClick={() => {
                      if (!confirmLeaving('Switching to email and password clears what you have entered so far. Continue?')) return;
                      // Clear everything the confirm promised: the fields (Formik
                      // reinitialises), the photo, the video and any error.
                      setSubmissionError('');
                      setAvatar({});
                      setIntroVideo(undefined);
                      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
                    }}
                  >
                    Use email and password instead
                  </Button>
                </Box>
              </Box>
            )}
            <Formik
              initialValues={
                google
                  ? {
                      ...initialValues,
                      emailAddress: google.email,
                      firstName: google.firstName,
                      lastName: google.lastName,
                    }
                  : initialValues
              }
              validationSchema={google ? googleRegistrationSchema : registrationSchema}
              innerRef={formikRef}
              enableReinitialize
              onSubmit={submit}
              validateOnChange={false}
              validateOnBlur={false}
            >
              {({ isSubmitting, errors }) => (
                <Form>
                  {isSubmitting && (
                    // Scrim stays literal: bg-opacity-* cannot tint a var()-backed
                    // color, so bg-surface would drop the 80% translucency.
                    // Its copy stays text-dark1 for the same reason — the scrim is
                    // white in both themes, so a flipping text token would vanish.
                    <Box className="fixed inset-0 bg-white bg-opacity-80 backdrop-blur-sm z-50 flex justify-center items-center">
                      <DotLottleLoader />
                      <Box className="absolute inset-auto mt-[240px] items-center flex flex-col gap-0">
                        <Typography
                          variant="h6"
                          className="text-dark1 font-bold"
                          fontFamily="CircularStd"
                        >
                          {loadingMessage || 'Submitting your application'}...
                        </Typography>
                        {Boolean(loadingMessage) && (
                          <Typography
                            variant="body1"
                            className="text-dark1 font-bold"
                          >
                            Please do not close this window
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  <BasicInfo
                    // A fresh section per mode, so its own photo and video
                    // previews reset with the switch.
                    key={google ? 'google' : 'password'}
                    avatar={avatar}
                    setAvatar={setAvatar}
                    setIntro={setIntroVideo}
                    viaGoogle={!!google}
                  />
                  <Demographics />
                  <ContactInfo />
                  <Professions ref={professionsFormRef} />
                  <Qualifications />
                  <ExpertiseInfo />
                  <SocialReach />
                  {/* <MsaAgreement /> */}
                  {/* Validation Messages at the Bottom */}
                  {(Object.keys(errors).length > 0 ||
                    Boolean(submissionError)) && (
                    <Box className="mt-4 p-4 bg-down-bg border border-rose-200 rounded-lg text-down text-sm font-[CircularStd]">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold', marginBottom: '8px' }}
                      >
                        Please fix the following errors:
                      </Typography>
                      <ul className="list-disc pl-5">
                        {Object.keys(errors).map((key) => (
                          <li key={key}>
                            {errors[key as keyof typeof errors]}
                          </li>
                        ))}
                        {Boolean(submissionError) && (
                          <li
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                              __html: submissionError, // Render the HTML content
                            }}
                          />
                        )}
                      </ul>
                    </Box>
                  )}
                  <Box className="flex justify-center items-center sticky w-full p-4 bg-surface z-10">
                    <Button
                      size="large"
                      type="submit"
                      variant="contained"
                      sx={{
                        width: 'fit-content',
                        minWidth: '200px',
                      }}
                    >
                      Submit Application
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
            <Typography className="text-text-1 text-center text-lg font-semibold mt-2">
              Having trouble? Feel free to reach out to us at&nbsp;
              <a
                href="mailto:help@thedesirecompany.com"
                rel="noreferrer"
                target="_blank"
                style={{ color: 'var(--accent-text)', textDecoration: 'underline' }}
              >
                help@thedesirecompany.com
              </a>
            </Typography>
          </Box>
        </Container>
      </Box>
    </div>
  );
}
